import * as Tooltip from "@radix-ui/react-tooltip";
import { cn } from "@/lib/cn";
import type { ReactNode } from "react";

type Props = {
  content: ReactNode;
  variant?: "light" | "dark";
  side?: "top" | "right" | "bottom" | "left";
  children?: ReactNode;
};

/**
 * HelpTip — inline ⓘ button with a contextual tooltip.
 * Use next to labels where the prototype lacked explanation: DPI guidance,
 * locked-content rationale, pricing-tier rules, etc.
 *
 * Wraps Radix Tooltip — keyboard accessible by default.
 */
export function HelpTip({ content, variant = "light", side = "top", children }: Props) {
  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          {children ?? (
            <button
              type="button"
              aria-label="More info"
              className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-current text-2xs font-bold leading-none text-fg-3 hover:text-fg-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus)]"
            >
              i
            </button>
          )}
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side={side}
            sideOffset={6}
            className={cn(
              "z-50 max-w-xs rounded-md px-3 py-2 text-xs leading-snug shadow-lg",
              variant === "dark"
                ? "bg-[var(--fg-1)] text-white"
                : "border border-[var(--border-1)] bg-white text-fg-1",
            )}
          >
            {content}
            <Tooltip.Arrow
              className={variant === "dark" ? "fill-[var(--fg-1)]" : "fill-white"}
              width={10}
              height={5}
            />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
